import type {
  DocumentComment,
  DrawAnnotationMarkerInstruction,
  EditEntries,
  EditEntryOrGroup,
} from '@aline/common';
import { findSuggestedEditIndex, groupEditEntries, isEditEntryGroup, Queries } from '@aline/common';
import type { Editor } from 'slate';
import { Point, Range } from 'slate';

const MARKER_THRESHOLD_PX = 32;

export interface GroupedAnnotationMarker {
  commentIds: Set<string>;
  commentRanges: Range[];
  editItems: EditEntryOrGroup[];
  y: number;
}

export function groupAnnotationMarkers({
  annotationMarkers,
  editEntries,
  editor,
}: {
  annotationMarkers: DrawAnnotationMarkerInstruction[];
  editEntries: EditEntries;
  editor: Editor;
}) {
  const sortedAnnotationMarkers = annotationMarkers.toSorted((a, b) => a.y - b.y);

  // Match each marker to the edits it surfaces once; both uses below read this map.
  const editEntriesByMarker = new Map<DrawAnnotationMarkerInstruction, EditEntries>();
  for (const marker of sortedAnnotationMarkers) {
    editEntriesByMarker.set(
      marker,
      editEntries.filter(
        entry =>
          findSuggestedEditIndex(marker.suggestedEdits, entry.edit) !== -1 &&
          Range.intersection(entry.range, marker.cMRange)
      )
    );
  }

  /**
   * Restrict super-grouping to edits this page's markers actually surface. This
   * also drops stale entries that redux-derived `editEntries` still carries but
   * the editor already removed (mid-render during fast deletes) — their invalid
   * paths would crash `groupEditEntries`/`pathIsAbutting` ("Cannot find a
   * descendant at path …").
   */
  const matchedEditEntries = new Set([...editEntriesByMarker.values()].flat());
  const relevantEditEntries = editEntries.filter(entry => matchedEditEntries.has(entry));

  /**
   * Group edits into the same suggestions the tracked changes pane shows, then
   * index each entry by its super-group. This collapses edits from one
   * suggestion into a single marker even when they span many lines (e.g.
   * consecutive paragraph insertions) — proximity alone can't, since the
   * pilcrows sit lines apart.
   */
  const superGroups = groupEditEntries(editor, relevantEditEntries);
  const superGroupIndexByEditEntry = new Map<EditEntries[number], number>();
  superGroups.forEach((item, index) => {
    const entries = isEditEntryGroup(item) ? item.editEntries : [item];
    for (const entry of entries) superGroupIndexByEditEntry.set(entry, index);
  });

  const groupedAnnotationMarkerData: GroupedAnnotationMarkerData[] = [];
  const seenCommentIds = new Set<string>();
  const assignedEditEntries = new Set<EditEntries[number]>();

  for (const annotationMarker of sortedAnnotationMarkers) {
    const { cMRange, commentIds, y } = annotationMarker;

    const newCommentIds = new Set(commentIds).difference(seenCommentIds);
    const markerEditEntries = (editEntriesByMarker.get(annotationMarker) ?? []).filter(
      editEntry => !assignedEditEntries.has(editEntry)
    );

    if (!newCommentIds.size && !markerEditEntries.length) continue;

    const markerSuperGroupIndices = new Set<number>();
    for (const entry of markerEditEntries) {
      const index = superGroupIndexByEditEntry.get(entry);
      if (index !== undefined) markerSuperGroupIndices.add(index);
    }

    /**
     * Prefer merging into a marker that already holds the same super-group,
     * regardless of vertical distance; otherwise fall back to the proximity
     * threshold (which clusters comment-only markers and unrelated edits).
     */
    let targetMarker = groupedAnnotationMarkerData.find(group =>
      [...group.superGroupIndices].some(index => markerSuperGroupIndices.has(index))
    );
    if (!targetMarker) {
      const lastMarker = groupedAnnotationMarkerData.at(-1);
      if (lastMarker && y - lastMarker.y <= MARKER_THRESHOLD_PX) targetMarker = lastMarker;
    }
    if (!targetMarker) {
      targetMarker = {
        commentIds: new Set(),
        commentRanges: [],
        superGroupIndices: new Set(),
        y,
      };
      groupedAnnotationMarkerData.push(targetMarker);
    }

    if (newCommentIds.size) targetMarker.commentRanges.push(cMRange);
    for (const index of markerSuperGroupIndices) targetMarker.superGroupIndices.add(index);

    for (const commentId of newCommentIds) {
      targetMarker.commentIds.add(commentId);
      seenCommentIds.add(commentId);
    }

    for (const editEntry of markerEditEntries) {
      assignedEditEntries.add(editEntry);
    }
  }

  return groupedAnnotationMarkerData.map(markerData => ({
    commentIds: markerData.commentIds,
    commentRanges: markerData.commentRanges,
    editItems: [...markerData.superGroupIndices]
      .sort((a, b) => a - b)
      .map(index => superGroups[index]),
    y: markerData.y,
  }));
}

export function getNextEditRange(
  selection: Range | null,
  editItems: EditEntryOrGroup[]
): Range | null {
  /**
   * Flatten to per-range slots in document order so multi-range groups
   * (e.g. a single deletion split by an insertion in the middle, which
   * arrives as one EditEntryGroup with two ranges) cycle through each
   * part separately instead of always landing on the group's first range.
   */
  const ranges = editItems.flatMap(getEditItemRanges).toSorted(compareFnSortRanges);
  if (!ranges.length) return null;
  if (!selection) return ranges[0];

  // Innermost surrounding range wins (nesting always puts the outer first).
  const activeIndex = ranges.findLastIndex(range => Range.surrounds(range, selection));
  if (activeIndex !== -1) return ranges[(activeIndex + 1) % ranges.length];

  const selectionStart = Range.start(selection);
  const nextRange = ranges.find(range => Point.compare(Range.start(range), selectionStart) > 0);
  return nextRange ?? ranges[0];
}

/**
 * The comment a marker click lands on: the shortest unresolved comment under the selection, the
 * same rule `useSetActiveComment` uses to pick the active comment. Resolved comments are skipped
 * because they never become active and the comments list hides them by default, so a request for
 * one would never find its card.
 */
export function getSelectedMarkerCommentId(
  editor: Editor,
  commentsById: ReadonlyMap<string, Pick<DocumentComment, 'resolved'>>
) {
  const unresolvedSelectedCommentIds = Queries.getSelectedCommentIds(editor).filter(
    commentId => !commentsById.get(commentId)?.resolved
  );
  return Queries.getShortestCommentId(editor, { commentIds: unresolvedSelectedCommentIds });
}

interface GroupedAnnotationMarkerData {
  commentIds: Set<string>;
  commentRanges: Range[];
  superGroupIndices: Set<number>;
  y: number;
}

function getEditItemRanges(editItem: EditEntryOrGroup) {
  if (isEditEntryGroup(editItem)) return editItem.editEntries.map(editEntry => editEntry.range);
  return [editItem.range];
}

function compareFnSortRanges(a: Range, b: Range): number {
  const startCmp = Point.compare(Range.start(a), Range.start(b));
  if (startCmp !== 0) return startCmp;
  // Outer range first when starts tie, so findLastIndex picks the innermost.
  return -Point.compare(Range.end(a), Range.end(b));
}
